import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import i18next from 'i18next';
import type { Field, FormSections } from 'korint-sdk/react';
import { type CountryCode, isSupportedCountry } from 'libphonenumber-js/max';
import { parseAndValidatePhone } from '../../../lib/phone-validation';
import { validatePostcode } from '../../../lib/postcode-validation';
import { z } from 'zod';
import { isFieldVisible } from './conditionalVisibilityUtils';
import { parseDateLimit as parseLimit } from '@folio/utils/date-limits';
import { buildAllFieldsFromSections } from './form-fields-builders';
import 'dayjs/locale/fr';

// Type mapping for field types to TypeScript types
type FieldTypeMap = {
  text: string;
  textWithTransform: string;
  number: number;
  email: string;
  select: string;
  multiSelect: string[];
  phoneNumber: string;
  postcode: string;
  company: { siret: string };
  checkbox: boolean;
  date: string;
  datetime: string;
  radio: string;
  vehicleVersion: string;
};

type ZodError = { type: string; message: string };

// Utility type to infer form data from fields
export type InferFormDataFromFields<T extends readonly Field[]> = {
  [K in T[number] as K['name']]: K['required'] extends false
    ? K['type'] extends keyof FieldTypeMap
      ? FieldTypeMap[K['type']] | undefined
      : never
    : K['type'] extends keyof FieldTypeMap
      ? FieldTypeMap[K['type']]
      : never;
};

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Without this, a value outside the options (e.g. prefilled from an API) surfaces
// Zod's raw English "Invalid enum value. Expected …" to the user.
const enumErrorMap: z.ZodErrorMap = (issue, ctx) => {
  if (
    issue.code === z.ZodIssueCode.invalid_enum_value ||
    issue.code === z.ZodIssueCode.invalid_type
  ) {
    return { message: i18next.t('formErrors.selectOption') };
  }
  return { message: ctx.defaultError };
};

const getBaseSchemaFromField = (field: Field): z.ZodTypeAny => {
  switch (field.type) {
    case 'textWithTransform':
    case 'textarea':
    case 'text': {
      let schema = z.string({
        required_error: i18next.t('formErrors.required'),
        // invalid_type_error only happens when composing FormBuilder with custom inputs like VehicleBrandFormField in axece NewBusinessBrokerVehicle.tsx
        invalid_type_error: i18next.t('formErrors.required'),
      });

      if (field.minLength !== undefined) {
        schema = schema.min(
          field.minLength,
          i18next.t('formErrors.tooShort', { count: field.minLength }),
        );
      }
      if (field.maxLength !== undefined) {
        schema = schema.max(
          field.maxLength,
          i18next.t('formErrors.tooLong', { count: field.maxLength }),
        );
      }

      if (!field.required) {
        return schema.optional();
      }

      return schema.min(1, i18next.t('formErrors.required'));
    }

    case 'number': {
      // Auto-computed fields are derived and read-only — never validate them as user input.
      if (field.autoComputed) {
        return z.any();
      }

      let schema: z.ZodNumber | z.ZodEffects<z.ZodNumber, number, number> = z.number({
        required_error: i18next.t('formErrors.required'),
        invalid_type_error: i18next.t('formErrors.invalidNumber'),
        coerce: true,
      });

      if (field.integer) {
        schema = schema.int(i18next.t('formErrors.mustBeInteger'));
      }

      if (field.min !== undefined) {
        schema = schema.min(field.min, i18next.t('formErrors.min', { value: field.min }));
      }

      if (field.max !== undefined) {
        schema = schema.max(field.max, i18next.t('formErrors.max', { value: field.max }));
      }

      if (field.minLength !== undefined || field.maxLength !== undefined) {
        schema = schema.superRefine((val, ctx) => {
          const stringValue = String(val);
          if (field.minLength !== undefined && stringValue.length < field.minLength) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_small,
              minimum: field.minLength,
              type: 'string',
              inclusive: true,
              message: i18next.t('formErrors.tooShort', { count: field.minLength }),
            });
          }
          if (field.maxLength !== undefined && stringValue.length > field.maxLength) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_big,
              maximum: field.maxLength,
              type: 'string',
              inclusive: true,
              message: i18next.t('formErrors.tooLong', { count: field.maxLength }),
            });
          }
        });
      }

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'email': {
      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
        })
        .email(i18next.t('formErrors.invalidEmail'));

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'select': {
      if (!field.required) {
        return z
          .enum(
            [
              '',
              ...field.options.map(option => (typeof option === 'string' ? option : option.value)),
            ],
            { errorMap: enumErrorMap },
          )
          .optional();
      }

      return z.enum(
        field.options.map(option => (typeof option === 'string' ? option : option.value)) as [
          string,
          ...string[],
        ],
        { errorMap: enumErrorMap },
      );
    }

    case 'multiSelect': {
      const validValues = field.options.map(option =>
        typeof option === 'string' ? option : option.value,
      );

      // Préprocesseur pour transformer les objets en strings
      const schema = z.preprocess(
        val => {
          if (!Array.isArray(val)) return val;
          // Transformer chaque élément de l'array
          return val.map(item => {
            if (typeof item === 'object' && item !== null && 'value' in item) {
              return (item as { value: string }).value;
            }
            return item;
          });
        },
        validValues.length > 1
          ? z.array(z.enum(validValues as [string, ...string[]]), {
              required_error: i18next.t('formErrors.selectAtLeastOne'),
              invalid_type_error: i18next.t('formErrors.selectAtLeastOne'),
            })
          : z.array(z.literal(validValues[0]), {
              required_error: i18next.t('formErrors.selectAtLeastOne'),
              invalid_type_error: i18next.t('formErrors.selectAtLeastOne'),
            }),
      );

      // Si le champ est requis, on impose au moins une sélection
      if (field.required) {
        const requiredSchema = schema.refine(arr => arr.length > 0, {
          message: i18next.t('formErrors.selectAtLeastOne'),
        });
        return requiredSchema;
      }

      return schema.optional();
    }

    case 'phoneNumber': {
      if (field.countries?.some(c => !isSupportedCountry(c.toUpperCase()))) {
        throw new Error('Unsupported country code provided.');
      }

      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
        })
        .transform<boolean extends true ? string : string | undefined>(
          (v, ctx): boolean extends true ? string : string | undefined => {
            if (!v) {
              if (field.required) {
                ctx.addIssue({
                  code: 'custom',
                  message: i18next.t('formErrors.required'),
                  fatal: true,
                });
              }

              return undefined as boolean extends true ? string : string | undefined;
            }

            return parseAndValidatePhone(
              v,
              ctx,
              field.countries as CountryCode[] | undefined,
            ) as boolean extends true ? string : string | undefined;
          },
        );

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'postcode': {
      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
        })
        .superRefine((value, ctx) => {
          if (!value) {
            if (field.required) {
              ctx.addIssue({
                code: 'custom',
                message: i18next.t('formErrors.required'),
                fatal: true,
              });
            }
            return;
          }
          if (!validatePostcode(value, field.countries)) {
            ctx.addIssue({
              code: 'custom',
              message: i18next.t(
                field.countries?.length
                  ? 'formErrors.postcodeInvalidCountry'
                  : 'formErrors.postcodeInvalid',
              ),
              fatal: true,
            });
          }
        });

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'company': {
      const schema = z
        .object(
          {
            siret: z.string({ required_error: i18next.t('formErrors.required') }),
          },
          {
            required_error: i18next.t('formErrors.required'),
            invalid_type_error: i18next.t('formErrors.required'),
          },
        )
        .passthrough();

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'checkbox': {
      if (field.required) {
        return z.literal(true, {
          errorMap: () => ({
            message: field?.requiredErrorMessage ?? i18next.t('formErrors.criterionRequired'),
          }),
        });
      }

      return z.coerce.boolean();
    }

    case 'date': {
      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
          invalid_type_error: i18next.t('formErrors.invalidDate'),
        })
        .refine(
          value => dayjs(value, 'YYYY-MM-DD', true).isValid(),
          i18next.t('formErrors.invalidDate'),
        )
        .refine(
          value => !field.max || dayjs(value).isSameOrBefore(parseLimit(field.max), 'day'),
          i18next.t('formErrors.dateBefore', {
            date: parseLimit(field.max)?.format('DD/MM/YYYY'),
          }),
        )
        .refine(
          value => dayjs(value).isSameOrAfter(parseLimit(field.min ?? '1900-01-01'), 'day'),
          i18next.t('formErrors.dateAfter', {
            date: parseLimit(field.min ?? '1900-01-01')?.format('DD/MM/YYYY'),
          }),
        );

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'month': {
      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
          invalid_type_error: i18next.t('formErrors.invalidMonth'),
        })
        .refine(v => dayjs(v, 'YYYY-MM', true).isValid(), i18next.t('formErrors.invalidMonth'))
        .refine(
          v => {
            const d = dayjs(v, 'YYYY-MM', true);
            return !field.max || d.isSameOrBefore(parseLimit(field.max), 'month');
          },
          i18next.t('formErrors.monthBefore', {
            date: parseLimit(field.max)?.locale(i18next.language)?.format('MMMM YYYY'),
          }),
        )
        .refine(
          v => {
            const d = dayjs(v, 'YYYY-MM', true);
            return d.isSameOrAfter(parseLimit(field.min ?? '1900-01-01'), 'month');
          },
          i18next.t('formErrors.monthAfter', {
            date: parseLimit(field.min ?? '1900-01-01')
              ?.locale(i18next.language)
              ?.format('MMMM YYYY'),
          }),
        );

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'datetime': {
      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
          invalid_type_error: i18next.t('formErrors.invalidDateTime'),
        })
        .refine(value => dayjs(value).isValid(), i18next.t('formErrors.invalidDateTime'))
        .refine(
          value => !field.max || dayjs(value).isSameOrBefore(parseLimit(field.max), 'minute'),
          i18next.t('formErrors.dateTimeBefore', {
            date: parseLimit(field.max)?.format('DD/MM/YYYY HH:mm'),
          }),
        )
        .refine(
          value => dayjs(value).isSameOrAfter(parseLimit(field.min ?? '1900-01-01'), 'minute'),
          i18next.t('formErrors.dateTimeAfter', {
            date: parseLimit(field.min ?? '1900-01-01')?.format('DD/MM/YYYY HH:mm'),
          }),
        );

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'radio':
    case 'radioBlock': {
      const schema = z.enum(
        field.options.map(option => (typeof option === 'string' ? option : option.value)) as [
          string,
          ...string[],
        ],
        { errorMap: enumErrorMap },
      );

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'vehicleVersion': {
      const schema = z.string({
        required_error: i18next.t('formErrors.required'),
        invalid_type_error: i18next.t('formErrors.required'),
      });

      if (!field.required) {
        return schema.optional();
      }

      return schema.min(1, i18next.t('formErrors.required'));
    }

    case 'vehicleRegistration': {
      const schema = z
        .string({
          required_error: i18next.t('formErrors.required'),
          invalid_type_error: i18next.t('formErrors.required'),
        })
        .min(7, i18next.t('formErrors.vehicleRegistrationTooShort'));

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }
    case 'vehicleBrand':
    case 'vehicleModel': {
      const schema = z.string({
        required_error: i18next.t('formErrors.required'),
        invalid_type_error: i18next.t('formErrors.required'),
      });

      if (!field.required) {
        return schema.optional();
      }

      return schema;
    }

    case 'hiscoxActivity': {
      const schema = z.string({
        required_error: i18next.t('formErrors.required'),
        invalid_type_error: i18next.t('formErrors.required'),
      });

      if (!field.required) {
        return schema.optional().nullable();
      }

      return schema;
    }

    case 'hiscoxMrpExcess': {
      const values = field.options.map(option => option.value) as [string, ...string[]];

      if (!field.required) {
        return z.enum(['', ...values], { errorMap: enumErrorMap }).optional();
      }

      return z.enum(values, { errorMap: enumErrorMap });
    }

    case 'computed': {
      return z.unknown();
    }

    case 'readonly': {
      return z.unknown();
    }

    case 'address':
    case 'city':
    case 'country': {
      const schema = z.string({
        required_error: i18next.t('formErrors.required'),
        invalid_type_error: i18next.t('formErrors.required'),
      });

      if (!field.required) {
        return schema.optional();
      }

      return schema.min(1, i18next.t('formErrors.required'));
    }

    case 'vehicleInput': {
      return z.unknown();
    }

    case 'array': {
      const rowSchema = z.object(
        Object.fromEntries(
          field.columns.map(column => [
            column.name,
            z.preprocess(value => (value === '' ? undefined : value), getSchemaFromField(column)),
          ]),
        ),
      );
      let schema = z.array(rowSchema, { required_error: i18next.t('formErrors.required') });

      if (field.maxItems !== undefined) {
        schema = schema.max(
          field.maxItems,
          i18next.t('formErrors.arrayMaxItems', { count: field.maxItems }),
        );
      }

      if (!field.required) {
        return schema.optional();
      }

      return schema.min(1, i18next.t('formErrors.required'));
    }

    default:
      field satisfies never;
      return z.never({
        message: 'Not implemented',
      });
  }
};

export const getSchemaFromField = (field: Field): z.ZodTypeAny => {
  const schema = getBaseSchemaFromField(field);

  if (field.additionalValidationRules) {
    return field.additionalValidationRules(schema);
  }

  return schema;
};

function isDeletableFieldEmpty(field: Field, value: unknown): boolean {
  return !!(field.canBeDeleted && field.isCustomField && (value == null || value === ''));
}

export const getResolver = <T extends readonly Field[]>(fields: T) => {
  // Create a custom resolver that evaluates field visibility during validation
  return async (
    values: Record<string, unknown>,
    context: Record<string, unknown> = {},
  ): Promise<{
    values: InferFormDataFromFields<T>;
    errors: Record<string, ZodError>;
  }> => {
    const errors: Record<string, ZodError> = {};
    const parsedValues: Record<string, unknown> = {};

    for (const field of fields) {
      const isVisible = isFieldVisible(field, { ...context, ...values });

      if (field.type === 'computed' && !field.additionalValidationRules) {
        // Computed fields are not validated
        if (isVisible) {
          parsedValues[field.name] = values[field.name];
        }
        continue;
      }

      if (!isVisible) {
        continue;
      }

      let fieldValue = values[field.name];

      if (isDeletableFieldEmpty(field, fieldValue)) {
        parsedValues[field.name] = null;
        continue;
      }

      const fieldSchema = getSchemaFromField(field);

      if (field.type === 'computed') {
        const { formDataAccessor, name } = field;
        fieldValue =
          typeof formDataAccessor === 'function'
            ? formDataAccessor(values)
            : values[formDataAccessor ?? name];
      }
      const result = fieldSchema.safeParse(fieldValue);

      if (!result.success) {
        const firstError = result.error.errors[0];
        if (firstError) {
          errors[[field.name, ...firstError.path].join('.')] = {
            type: firstError.code,
            message: firstError.message,
          };
        }
      } else {
        // Use the successfully parsed/coerced value (e.g., string -> number)
        parsedValues[field.name] = result.data;
      }
    }

    return {
      values:
        Object.keys(errors).length === 0
          ? (parsedValues as InferFormDataFromFields<T>)
          : ({} as InferFormDataFromFields<T>),
      errors: Object.keys(errors).length > 0 ? errors : {},
    };
  };
};

export function getResolverFromSections(sections: FormSections) {
  return getResolver(buildAllFieldsFromSections(sections));
}

export const getArrayFieldsResolver = <T extends Record<string, unknown[]>>(
  fields: Field[],
  arrayFieldName = 'assets',
  minItems?: number,
  maxItems?: number,
) => {
  return (values: T) => {
    const errors: Record<string, ZodError> = {};
    const validatedItems: Record<string, unknown>[] = [];

    const arrayValues = values[arrayFieldName];

    if (arrayValues) {
      for (const [itemIndex, item] of arrayValues.entries()) {
        if (typeof item !== 'object' || item === null) {
          continue;
        }

        const validatedItem: Record<string, unknown> = { ...(item as Record<string, unknown>) };

        for (const field of fields) {
          const isVisible = isFieldVisible(field, item as Record<string, unknown>);

          if (isVisible) {
            const fieldValue = (item as Record<string, unknown>)[field.name];

            if (isDeletableFieldEmpty(field, fieldValue)) {
              validatedItem[field.name] = null;
              continue;
            }

            const fieldSchema = getSchemaFromField(field);
            const result = fieldSchema.safeParse(fieldValue);

            if (!result.success) {
              const firstError = result.error.errors[0];
              if (firstError) {
                errors[[arrayFieldName, itemIndex, field.name, ...firstError.path].join('.')] = {
                  type: firstError.code,
                  message: firstError.message,
                };
              }
            } else {
              validatedItem[field.name] = result.data;
            }
          }
        }

        validatedItems.push(validatedItem);
      }
    }

    if (minItems !== undefined && (!arrayValues || arrayValues.length < minItems)) {
      errors[arrayFieldName] = {
        type: 'too_small',
        message: i18next.t('formErrors.arrayMinItems', { count: minItems }),
      };
    }

    if (maxItems !== undefined && arrayValues && arrayValues.length > maxItems) {
      errors[arrayFieldName] = {
        type: 'too_big',
        message: i18next.t('formErrors.arrayMaxItems', { count: maxItems }),
      };
    }

    const hasErrors = Object.keys(errors).length > 0;

    return {
      values: hasErrors ? {} : { [arrayFieldName]: validatedItems },
      errors: hasErrors ? errors : {},
    };
  };
};
